"use client";

import { createChangeEventDetails } from "@base-ui/react/internals/createBaseUIEventDetails";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import * as React from "react";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export const TooltipProvider: typeof TooltipPrimitive.Provider =
  TooltipPrimitive.Provider;

type TooltipSide = NonNullable<TooltipPrimitive.Positioner.Props["side"]>;

/**
 * The side a `TooltipPopup` opens on when its caller names none. A region
 * pinned to a screen edge — an end-anchored panel, a bottom bar — sets it
 * once here so every unlabeled tooltip inside opens away from that edge; a
 * popup that names its own `side` still wins. Outside any region the
 * default is `top`.
 */
const TooltipSideContext = React.createContext<TooltipSide>("top");

export function TooltipSideProvider({
  side,
  children,
}: Omittable<{
  side: TooltipSide;
  children: React.ReactNode;
}>): React.ReactElement {
  return (
    <TooltipSideContext.Provider value={side}>
      {children}
    </TooltipSideContext.Provider>
  );
}

/**
 * The touch path into a tooltip. Base UI opens a tooltip on hover and on
 * keyboard focus only (`mouseOnly`), so on a coarse pointer an icon-only
 * control's tooltip — the only visible name it has — is unreachable. The
 * platform gesture for "read this control's label" is press-and-hold: the
 * bubble shows while the finger rests and clears on release, and the release
 * does NOT activate the control (that is what a plain tap does). The root
 * provides this to its trigger; `null` on a hover-capable device, where the
 * trigger stays exactly Base UI's.
 */
type TooltipPressReveal = {
  show: (event: PointerEvent, trigger: HTMLElement) => void;
  hide: (event: PointerEvent, trigger: HTMLElement) => void;
};
const TooltipPressRevealContext =
  React.createContext<TooltipPressReveal | null>(null);

/** Matches Base UI's context-menu long press, so a held press on a labeled
 *  control reads its label in the same beat a held press on the region
 *  around it would open its menu. */
const PRESS_REVEAL_DELAY = 500;
/** Travel past this cancels the press: the finger is scrolling, not
 *  holding. Base UI's context menu uses the same tolerance. */
const PRESS_REVEAL_TOLERANCE = 10;

/**
 * Tooltips here are never interactive — the popup is `pointer-events-none`
 * (see TooltipPopup) — so Base UI's hoverable-popup hold, which keeps the
 * bubble open while the pointer travels from the trigger toward it, is pure
 * downside: a side-anchored bubble covers the neighboring control in that
 * direction, and the stale tooltip lingers over it while blocking its hover
 * (the kanban card's left-anchored property labels are the canonical case —
 * scanning the footer right-to-left, each label parked itself on top of the
 * next action). Disable the hold so leaving the trigger closes immediately;
 * a caller building a genuinely hoverable tooltip must also restore pointer
 * events on the popup, so pass `disableHoverablePopup={false}` AND a
 * `pointer-events-auto` popup className together.
 *
 * `open` is resolved here rather than left to Base UI so the press-reveal
 * has a way in: the held press reports through the SAME `onOpenChange` the
 * hover path uses (reason `trigger-press`), so a caller that controls the
 * tooltip — or reads the trigger's text as it opens — sees a press exactly
 * as it sees a hover, and keeps its veto.
 */
export function Tooltip({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  disabled,
  ...props
}: TooltipPrimitive.Root.Props): React.ReactElement {
  const isTouch = useIsTouchDevice();
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;

  const handleOpenChange = React.useCallback<
    NonNullable<TooltipPrimitive.Root.Props["onOpenChange"]>
  >(
    (next, details) => {
      // A coarse pointer has no hover, so a hover-open there is the
      // compatibility mouse trail a touch release synthesizes (mouseover /
      // mouseenter after touchend) — which lands right after the press-reveal
      // closed the bubble and would re-open it under a finger that has left.
      // Refused, not merely ignored: `cancel` keeps Base UI's own state in
      // step with the `open` this root resolves.
      if (next && isTouch && details.reason === "trigger-hover") {
        details.cancel();
        return;
      }
      onOpenChange?.(next, details);
      if (!controlled) setUncontrolledOpen(next);
    },
    [controlled, isTouch, onOpenChange],
  );

  const pressReveal = React.useMemo<TooltipPressReveal | null>(() => {
    if (!isTouch || disabled) return null;
    const report = (next: boolean, event: PointerEvent, trigger: HTMLElement) =>
      handleOpenChange(
        next,
        createChangeEventDetails<
          "trigger-press",
          { preventUnmountOnClose(): void }
        >("trigger-press", event, trigger, { preventUnmountOnClose() {} }),
      );
    return {
      show: (event, trigger) => report(true, event, trigger),
      hide: (event, trigger) => report(false, event, trigger),
    };
  }, [isTouch, disabled, handleOpenChange]);

  return (
    <TooltipPressRevealContext.Provider value={pressReveal}>
      <TooltipPrimitive.Root
        disableHoverablePopup
        disabled={disabled}
        onOpenChange={handleOpenChange}
        open={open}
        {...props}
      />
    </TooltipPressRevealContext.Provider>
  );
}

/**
 * Press-and-hold handlers for a trigger on a coarse pointer. Arms on
 * pointerdown, reveals after the delay, clears on release or on travel; a
 * release after a reveal swallows the click that follows it, so reading a
 * label never activates the control. Only the INNERMOST enabled tooltip
 * trigger under the finger arms — the rule hover already follows, where an
 * enclosing trigger opens only while the pointer is not over an enabled
 * nested one — so a clipped value inside a labeled row reads the value.
 * The arming trigger also stops `touchstart` from reaching an enclosing
 * context-menu trigger: a held press on a labeled control reads the label,
 * and the region's menu stays for a held press beside the control.
 */
function usePressReveal(
  reveal: TooltipPressReveal | null,
): React.HTMLAttributes<HTMLElement> | undefined {
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const originRef = React.useRef<{ x: number; y: number } | null>(null);
  const revealedRef = React.useRef(false);
  const armedRef = React.useRef(false);

  const clear = React.useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    originRef.current = null;
    armedRef.current = false;
  }, []);

  React.useEffect(() => clear, [clear]);

  return React.useMemo(() => {
    if (!reveal) return undefined;
    const release = (e: React.PointerEvent<HTMLElement>) => {
      clear();
      if (revealedRef.current) reveal.hide(e.nativeEvent, e.currentTarget);
    };
    return {
      onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
        // Every press starts clean: a reveal whose release synthesized no
        // click (the platform showed its own context menu instead) must not
        // swallow the NEXT tap's events.
        revealedRef.current = false;
        // Keyed on this file's own stamp, not Base UI's `data-trigger-
        // disabled`: that one follows the root's store a render late, and
        // a value whose clipping was measured on this very contact must
        // already read as enabled when the press arrives.
        const target = e.target as Element | null;
        const innermost = target?.closest("[data-press-reveal]");
        if (innermost !== e.currentTarget) return;
        clear();
        armedRef.current = true;
        originRef.current = { x: e.clientX, y: e.clientY };
        const { nativeEvent, currentTarget } = e;
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          revealedRef.current = true;
          reveal.show(nativeEvent, currentTarget);
        }, PRESS_REVEAL_DELAY);
      },
      onTouchStart: (e: React.TouchEvent<HTMLElement>) => {
        if (armedRef.current) e.stopPropagation();
      },
      onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
        const origin = originRef.current;
        if (!origin || timerRef.current === null) return;
        if (
          Math.abs(e.clientX - origin.x) > PRESS_REVEAL_TOLERANCE ||
          Math.abs(e.clientY - origin.y) > PRESS_REVEAL_TOLERANCE
        ) {
          clear();
        }
      },
      onPointerUp: release,
      onPointerCancel: release,
      onPointerLeave: release,
      onContextMenu: (e: React.MouseEvent<HTMLElement>) => {
        if (revealedRef.current) e.preventDefault();
      },
      // The compatibility mouse events a touch release synthesizes arrive
      // mousedown → mouseup → click. A menu trigger opens on the mousedown,
      // so swallowing the click alone would leave the menu open under a
      // finger that only asked for the label; both are swallowed, and the
      // click — the last of them — clears the latch.
      onMouseDownCapture: (e: React.MouseEvent<HTMLElement>) => {
        if (!revealedRef.current) return;
        e.preventDefault();
        e.stopPropagation();
      },
      onClickCapture: (e: React.MouseEvent<HTMLElement>) => {
        if (!revealedRef.current) return;
        revealedRef.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
    };
  }, [reveal, clear]);
}

type PressRevealHandlers = ReturnType<typeof usePressReveal>;

/** The caller's handler runs after the press-reveal's for the same event —
 *  the reveal's own handlers only arm timers and latch refs, so order does
 *  not change what the caller observes. */
function withPressReveal(
  props: TooltipPrimitive.Trigger.Props,
  reveal: NonNullable<PressRevealHandlers>,
): TooltipPrimitive.Trigger.Props {
  const merged: Record<string, unknown> = { ...props };
  const own = props as Record<string, unknown>;
  for (const key of Object.keys(reveal) as (keyof typeof reveal)[]) {
    const ours = reveal[key] as (e: unknown) => void;
    const theirs = own[key] as ((e: unknown) => void) | undefined;
    merged[key] = theirs
      ? (e: unknown) => {
          ours(e);
          theirs(e);
        }
      : ours;
  }
  return merged as TooltipPrimitive.Trigger.Props;
}

/**
 * Focus that is not physically inside the trigger is not the trigger's
 * focus. React bubbles focus through the REACT tree, so an overlay a control
 * inside the trigger opened — a popover whose search field auto-focuses, a
 * dialog's first tabbable — reports its focus here from under
 * `document.body`, and Base UI's focus-open reads that field (a text field
 * always matches `:focus-visible`) as keyboard focus on the trigger: the
 * tooltip opens the instant the popover does and closes on the button's own
 * blur a frame later — a flash beside the surface the press just opened. The
 * same containment test gates blur, so the pair stays symmetric.
 */
function isOwnFocusEvent(event: React.FocusEvent<HTMLElement>): boolean {
  return event.currentTarget.contains(event.target as Node);
}

export function TooltipTrigger({
  onFocus,
  onBlur,
  ...rest
}: TooltipPrimitive.Trigger.Props): React.ReactElement {
  const pressReveal = usePressReveal(
    React.useContext(TooltipPressRevealContext),
  );
  const props: TooltipPrimitive.Trigger.Props = {
    ...rest,
    onFocus: (event) => {
      if (!isOwnFocusEvent(event)) event.preventBaseUIHandler();
      onFocus?.(event);
    },
    onBlur: (event) => {
      if (!isOwnFocusEvent(event)) event.preventBaseUIHandler();
      onBlur?.(event);
    },
  };
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      // Present exactly while a held press would reveal this tooltip: the
      // stamp an enclosing trigger reads to yield the press to this one.
      data-press-reveal={pressReveal ? "" : undefined}
      {...(pressReveal ? withPressReveal(props, pressReveal) : props)}
    />
  );
}

/**
 * The caret glyph is 14 wide and 8 deep. Its box starts at the popup's
 * padding edge — an absolutely positioned child measures from there, INSIDE
 * the 1px border — so the glyph's first row is the hairline it interrupts
 * and the tip protrudes 7 past the bubble's outer edge. The per-side offset
 * classes on the arrow and the arrow-on default `sideOffset` both derive
 * from these.
 */
const ARROW_LENGTH = 14;
const ARROW_DEPTH = 8;
const POPUP_BORDER = 1;
const ARROW_PROTRUSION = ARROW_DEPTH - POPUP_BORDER;
/**
 * `sideOffset` is the visible gap between the trigger and the bubble's
 * nearest point: the border edge for the plain bubble, the caret's tip when
 * the caret is on. The plain bubble floats 4px off; the caret nearly touches
 * what it points at. A caller's explicit `sideOffset` is read the same way,
 * so an offset tuned to clear a bar's edge clears it with either bubble.
 */
const SIDE_OFFSET = 4;
const ARROW_TIP_GAP = 1;

/**
 * Two paths over one triangle so the caret reads as the bubble's own edge.
 * The fill takes the popover fill; its full-width base row is the hairline
 * row, so it paints over the segment the caret interrupts. The open stroke
 * traces the two slanted sides in the popover hairline color, starting half
 * a stroke down so its width sits exactly on that row and nothing of it
 * bleeds into the bubble's interior. Drawn pointing DOWN, i.e. for
 * `data-side="top"`; the other sides rotate it.
 */
function TooltipArrowGlyph(): React.ReactElement {
  const tipY = ARROW_DEPTH - 1;
  const half = ARROW_LENGTH / 2;
  const tip = `Q${half} ${tipY + 0.4} ${half + 0.6} ${tipY - 0.4}`;
  const fill = `M0 0 H${ARROW_LENGTH} L${half + 0.6} ${tipY - 0.4} Q${half} ${tipY + 0.4} ${half - 0.6} ${tipY - 0.4} Z`;
  const outline = `M0 0.5 L${half - 0.6} ${tipY - 0.4} ${tip} L${ARROW_LENGTH} 0.5`;
  return (
    <svg
      aria-hidden="true"
      className="block overflow-visible"
      fill="none"
      height={ARROW_DEPTH}
      viewBox={`0 0 ${ARROW_LENGTH} ${ARROW_DEPTH}`}
      width={ARROW_LENGTH}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path className="fill-popover" d={fill} />
      <path
        className="stroke-border"
        d={outline}
        strokeLinejoin="round"
        strokeWidth="1"
      />
    </svg>
  );
}

/**
 * The width of the widest line laid out inside `container`: the span from
 * the leftmost to the rightmost edge of every text fragment and inline box,
 * so a line that mixes words with a Kbd chip counts whole, and centered or
 * RTL lines measure the same as start-aligned ones. A block-level child
 * reports its full box — the container's width — so a bubble composed of
 * blocks measures as already fitted and is left alone.
 */
function widestLineWidth(container: HTMLElement): number {
  const range = container.ownerDocument.createRange();
  range.selectNodeContents(container);
  let left = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;
  for (const rect of range.getClientRects()) {
    if (rect.width === 0) continue;
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.right);
  }
  return right > left ? right - left : 0;
}

/**
 * `text-balance` evens the lines of a wrapped label but leaves the box at
 * `max-w-xs`: the browser balances INSIDE the width it was given and never
 * shrinks the block to what the lines used, so a two-line tooltip paints a
 * band of empty bubble to the right of both lines. No CSS closes that gap —
 * `fit-content` is clamped by the same max-width the text balanced in — so
 * the bubble is fitted here: the widest line is measured once the content
 * has laid out, and the popup's max-width is set to it plus the bubble's
 * own padding and border. Balancing again at exactly that width reproduces
 * the same breaks (it is the narrowest width that keeps the line count), so
 * the words stay put and only the box closes in on them; a one-line bubble
 * measures its own width and is left alone.
 *
 * A callback ref on the viewport, not an effect: React attaches a host
 * node's ref before it runs the layout effects of the component that
 * rendered it, so this runs after the content is in the DOM and BEFORE Base
 * UI's auto-resize measures the popup and sizes the positioner from it —
 * the one window in which the fitted width can shape the positioner too.
 * Fitted after that, the popup would narrow inside a positioner still as
 * wide as the unfitted bubble, and sit off-center under its trigger. Runs
 * once per open (the popup remounts each time); a second call, as under
 * StrictMode, finds the content already fitted and does nothing.
 */
function fitBubbleToLines(viewport: HTMLDivElement | null): void {
  const popup = viewport?.parentElement;
  const content = viewport?.querySelector<HTMLElement>(":scope > [data-current]");
  if (!popup || !content) return;
  // Client rects are transformed, and the popup opens in its starting
  // style (`scale-98`): measured through it every width reads 2% short,
  // the fitted box re-wraps the text, and the next measurement shrinks it
  // again. Base UI's own auto-resize measures with scale and transform
  // neutralized; so does this.
  const { scale, transform } = popup.style;
  popup.style.scale = "1";
  popup.style.transform = "none";
  const contentWidth = content.getBoundingClientRect().width;
  const widest = widestLineWidth(content);
  const chrome = popup.getBoundingClientRect().width - contentWidth;
  popup.style.scale = scale;
  popup.style.transform = transform;
  // A 1px tolerance: a single line's fragments measure its box within
  // subpixel rounding, and that line is the box.
  if (widest === 0 || widest >= contentWidth - 1) return;
  popup.style.maxWidth = `${Math.ceil(widest + chrome)}px`;
}

export function TooltipPopup({
  className,
  align = "center",
  sideOffset: sideOffsetProp,
  side: sideProp,
  anchor,
  arrow = true,
  children,
  portalProps,
  ...props
}: TooltipPrimitive.Popup.Props & Omittable<{
  align?: TooltipPrimitive.Positioner.Props["align"];
  /** Omitted, the enclosing `TooltipSideProvider`'s side, else `top`. */
  side?: TooltipPrimitive.Positioner.Props["side"];
  sideOffset?: TooltipPrimitive.Positioner.Props["sideOffset"];
  anchor?: TooltipPrimitive.Positioner.Props["anchor"];
  /**
   * The caret on the bubble's edge, pointing at the trigger — on by default.
   * It follows the side the popup is actually rendered on — a collision flip
   * moves the caret with the bubble — so `side` is the only placement input.
   * `arrow={false}` is the plain bubble, for a tooltip that labels a region
   * rather than a control.
   */
  arrow?: boolean;
  portalProps?: TooltipPrimitive.Portal.Props;
}>): React.ReactElement {
  const regionSide = React.useContext(TooltipSideContext);
  const requestedSide = sideProp ?? regionSide;
  // A finger reads a label by holding the trigger (`usePressReveal`), and
  // the hand holding it covers what lies BELOW: a bubble that opens under a
  // toolbar action on a phone opens under the thumb. So on a coarse pointer
  // `bottom` becomes `top` — the one side a hand never covers — whatever the
  // caller or region asked for; the side sides stay, and the collision flip
  // still brings a bubble with no room above back down.
  const isTouch = useIsTouchDevice();
  const side = isTouch && requestedSide === "bottom" ? "top" : requestedSide;
  // The caret protrudes past the bubble, so the positioner stands that
  // protrusion further off and the requested gap lands at the caret's tip.
  const gap = sideOffsetProp ?? (arrow ? ARROW_TIP_GAP : SIDE_OFFSET);
  const sideOffset: typeof gap = !arrow
    ? gap
    : typeof gap === "function"
      ? (data) => gap(data) + ARROW_PROTRUSION
      : gap + ARROW_PROTRUSION;
  return (
    <TooltipPrimitive.Portal {...portalProps}>
      <TooltipPrimitive.Positioner
        align={align}
        anchor={anchor}
        // pointer-events-none (inherited by the popup): a tooltip is a label,
        // not a surface — an open bubble must never mask the control it
        // happens to cover, or that control's own tooltip/press is blocked
        // until this one dismisses.
        // z-60, the toast viewport's rung, not the popups' z-50: a tooltip
        // labels whatever is under the pointer, and that can be a word
        // inside a toast — at z-50 the bubble would open UNDER the toast
        // stack. Sharing the rung is safe because the bubble takes no input
        // (it can hide nothing from the pointer), and DOM order settles the
        // pair: the bubble mounts when it opens, after the viewport.
        // The position transition is the recenter/slide for an OPEN, settled
        // tooltip (content morphs resize the popup and the positioner glides
        // with it). It MUST stay gated on data-open: Floating UI parks an
        // unpositioned or closing positioner at a viewport corner
        // (position:fixed, 0/0), so an unconditional transition animates that
        // park — a dismissed tooltip flies across the screen, and a popup
        // mounted into an already-open root slides in from the corner.
        className="pointer-events-none z-60 h-(--positioner-height) w-(--positioner-width) max-w-(--available-width) data-open:not-data-instant:transition-[top,left,right,bottom,transform]"
        data-slot="tooltip-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <TooltipPrimitive.Popup
          className={cn(
            // The leading `dark` is deliberate: a tooltip is the classic dark
            // bubble in BOTH modes. The class flips every token in the
            // subtree (tokens.css scopes on .dark), so the fill, ink, and any
            // composed content (Kbd chips, muted text) all render with dark
            // values — in dark mode it's a no-op.
            // Kbd chips need a louder border + fill here than on other
            // surfaces: the popup is always dark, so their token-alpha defaults
            // (border white/15, bg white/5) nearly vanish against it.
            // max-w-xs: a tooltip is a label, and a label wraps to a few short
            // lines before it runs the width of the screen. Content longer than
            // that belongs in a popover, not here. A wrapped bubble is then
            // fitted to its widest balanced line (`fitBubbleToLines`), so the
            // cap is where the text breaks, never the width of the box.
            "dark relative flex h-(--popup-height,auto) w-(--popup-width,auto) max-w-xs origin-(--transform-origin) text-balance rounded-md border bg-popover text-popover-foreground text-xs font-medium shadow-card transition-[width,height,scale,opacity] before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-md)-1px)] before:shadow-[0_-1px_--theme(--color-white/6%)] data-ending-style:scale-98 data-starting-style:scale-98 data-ending-style:opacity-0 data-starting-style:opacity-0 data-instant:duration-0 [&_[data-slot=kbd]]:border-foreground/30 [&_[data-slot=kbd]]:bg-foreground/12",
            className,
          )}
          // The `dark` class above re-roots the theme, which cancels whatever
          // surface scope this popup inherited, so it names its own — the dark
          // popover hairline sits a rung above the page's, and composed
          // content (separators, Kbd edges) needs that louder line to read on
          // the always-dark bubble.
          data-on="popover"
          data-slot="tooltip-popup"
          // Base UI's popup sets no role. The bubble is the one visible name
          // of an icon-only control, so a screen reader and a test find it
          // by the ARIA tooltip role, named by its text.
          role="tooltip"
          {...props}
        >
          <TooltipPrimitive.Viewport
            // min-h-8 + centered content: the height a single line WITH a Kbd
            // chip (h-5 + py-1.5) produces is the floor for EVERY tooltip, so
            // a text-only bubble beside a shortcut-carrying one reads at the
            // same height instead of jittering 4px shorter. The previous/
            // current morph wrappers are unaffected — `previous` is absolute,
            // and `items-center` just centers the in-flow `current`.
            className="relative flex size-full min-h-8 items-center overflow-clip px-(--viewport-inline-padding) py-1.5 [--viewport-inline-padding:--spacing(3)] data-instant:transition-none **:data-current:data-ending-style:opacity-0 **:data-current:data-starting-style:opacity-0 **:data-previous:data-ending-style:opacity-0 **:data-previous:data-starting-style:opacity-0 **:data-current:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-previous:w-[calc(var(--popup-width)-2*var(--viewport-inline-padding)-2px)] **:data-previous:truncate **:data-current:opacity-100 **:data-previous:opacity-100 **:data-current:transition-opacity **:data-previous:transition-opacity"
            data-slot="tooltip-viewport"
            ref={fitBubbleToLines}
          >
            {children}
          </TooltipPrimitive.Viewport>
          {arrow ? (
            <TooltipPrimitive.Arrow
              // Floating UI centers the arrow on the trigger along the cross
              // axis with an inline `left`/`top`; these classes place it on
              // the main axis and turn the down-pointing glyph toward the
              // trigger. Offsets are from the padding edge: the box hangs
              // its full 8px depth past it, so its first row is the border
              // row. Rotation is about the box center, which keeps the
              // cross-axis centering for the sideways cases. There the 14x8
              // box reads 8 wide, so its 7px half-length, not its 4px
              // half-depth, sets the inset: the visual spans 0..8 past the
              // padding edge, center at 4, so the box edge sits at -(4 + 7).
              className={cn(
                "absolute flex",
                "data-[side=top]:-bottom-2",
                "data-[side=bottom]:-top-2 data-[side=bottom]:rotate-180",
                "data-[side=left]:-right-[11px] data-[side=left]:-rotate-90",
                "data-[side=right]:-left-[11px] data-[side=right]:rotate-90",
              )}
              data-slot="tooltip-arrow"
            >
              <TooltipArrowGlyph />
            </TooltipPrimitive.Arrow>
          ) : null}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}
